import { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Wifi, WifiOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Sparkline } from './Sparkline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SYMBOL_ORDER = [
  'EUR/USD', 'GBP/USD', 'USD/JPY', 'USD/CHF', 'AUD/USD', 'USD/CAD',
  'NZD/USD', 'EUR/GBP', 'XAU/USD', 'XAG/USD', 'BTC/USD', 'ETH/USD',
];

const TV_MAP: Record<string, string> = {
  'EUR/USD': 'OANDA:EURUSD',
  'GBP/USD': 'OANDA:GBPUSD',
  'USD/JPY': 'OANDA:USDJPY',
  'USD/CHF': 'OANDA:USDCHF',
  'AUD/USD': 'OANDA:AUDUSD',
  'USD/CAD': 'OANDA:USDCAD',
  'NZD/USD': 'OANDA:NZDUSD',
  'EUR/GBP': 'OANDA:EURGBP',
  'XAU/USD': 'OANDA:XAUUSD',
  'XAG/USD': 'OANDA:XAGUSD',
  'BTC/USD': 'BINANCE:BTCUSDT',
  'ETH/USD': 'BINANCE:ETHUSDT',
};

function getDecimals(symbol: string): number {
  if (symbol.startsWith('XAU') || symbol.startsWith('XAG') || symbol.startsWith('BTC') || symbol.startsWith('ETH')) return 2;
  if (symbol.includes('JPY')) return 2;
  return 4;
}

function getSpread(symbol: string, price: number): number {
  if (symbol === 'XAU/USD') return 0.30;
  if (symbol === 'XAG/USD') return 0.05;
  if (symbol === 'BTC/USD' || symbol === 'ETH/USD') return price * 0.001;
  return price * 0.0002; // ~2 pips for forex
}

interface PriceRow {
  symbol: string;
  price: number;
  change_percent: number;
  volume: number;
  updated_at: string;
}

export { SYMBOL_ORDER, TV_MAP };

export function ForexTicker() {
  const [prices, setPrices] = useState<Map<string, PriceRow>>(new Map());
  const [flashDir, setFlashDir] = useState<Map<string, 'up' | 'down'>>(new Map());
  const [history, setHistory] = useState<Map<string, number[]>>(new Map());
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState<number | null>(null);
  const prevPrices = useRef<Map<string, number>>(new Map());
  const navigate = useNavigate();

  const handleRow = useCallback((row: PriceRow) => {
    setPrices(prev => {
      const next = new Map(prev);
      next.set(row.symbol, row);
      return next;
    });

    const old = prevPrices.current.get(row.symbol);
    if (old !== undefined && row.price !== old) {
      setFlashDir(prev => {
        const next = new Map(prev);
        next.set(row.symbol, row.price > old ? 'up' : 'down');
        return next;
      });
      setTimeout(() => {
        setFlashDir(prev => {
          const next = new Map(prev);
          next.delete(row.symbol);
          return next;
        });
      }, 800);
    }
    prevPrices.current.set(row.symbol, row.price);

    setHistory(prev => {
      const next = new Map(prev);
      const arr = [...(prev.get(row.symbol) || []), row.price].slice(-10);
      next.set(row.symbol, arr);
      return next;
    });

    setLastSync(new Date(row.updated_at));
  }, []);

  // Initial fetch
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase.from('market_prices').select('*');
      if (data) {
        data.forEach((r) => handleRow(r as PriceRow));
      }
    };
    fetch();
  }, [handleRow]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('market_prices_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'market_prices' }, (payload) => {
        if (payload.new) handleRow(payload.new as PriceRow);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [handleRow]);

  // Tick seconds ago
  useEffect(() => {
    const t = setInterval(() => {
      if (lastSync) setSecondsAgo(Math.round((Date.now() - lastSync.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [lastSync]);

  const isStale = !lastSync || (Date.now() - lastSync.getTime() > 60_000);
  const orderedPrices = SYMBOL_ORDER.map(s => prices.get(s)).filter(Boolean) as PriceRow[];

  if (orderedPrices.length === 0) {
    return (
      <div className="bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-border/50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-foreground">Live Markets</h3>
          <Badge variant="outline" className="text-xs">
            <WifiOff className="w-3 h-3 mr-1" />
            Connecting...
          </Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-muted/50 rounded h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-foreground">Live Markets</h3>
            <Badge variant={isStale ? 'outline' : 'default'} className={`text-xs gap-1 ${isStale ? 'border-warning text-warning' : ''}`}>
              {isStale ? (
                <><WifiOff className="w-3 h-3" /> Stale</>
              ) : (
                <><Wifi className="w-3 h-3" /> Live</>
              )}
            </Badge>
          </div>
          {secondsAgo !== null && (
            <span className="text-xs text-muted-foreground">Synced {secondsAgo}s ago</span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <AnimatePresence>
            {orderedPrices.map(row => {
              const flash = flashDir.get(row.symbol);
              const decimals = getDecimals(row.symbol);
              const isUp = row.change_percent >= 0;
              const hist = history.get(row.symbol) || [];
              const tvSymbol = TV_MAP[row.symbol] || '';
              const spread = getSpread(row.symbol, row.price);
              const bid = row.price - spread / 2;
              const ask = row.price + spread / 2;
              const activityWidth = Math.min(Math.abs(row.change_percent) * 20, 100);

              return (
                <Tooltip key={row.symbol}>
                  <TooltipTrigger asChild>
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        backgroundColor: flash === 'up'
                          ? 'hsl(142 76% 36% / 0.15)'
                          : flash === 'down'
                          ? 'hsl(0 84% 60% / 0.15)'
                          : 'hsl(var(--muted) / 0.3)',
                      }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => navigate(`/trading-zone?symbol=${encodeURIComponent(tvSymbol)}`)}
                      className="p-3 rounded-lg border border-border/40 cursor-pointer hover:border-primary/40 overflow-hidden"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-muted-foreground">{row.symbol}</span>
                        {isUp ? <TrendingUp className="w-3 h-3 text-[hsl(var(--bull))]" /> : <TrendingDown className="w-3 h-3 text-[hsl(var(--bear))]" />}
                      </div>

                      {/* Price with flash indicator */}
                      <div className="flex items-center gap-1">
                        <span
                          className="text-base font-mono-data font-bold transition-colors duration-500"
                          style={{
                            color: flash === 'up'
                              ? 'hsl(142, 76%, 36%)'
                              : flash === 'down'
                              ? 'hsl(0, 84%, 60%)'
                              : undefined,
                          }}
                        >
                          {row.price.toFixed(decimals)}
                        </span>
                        {flash && (
                          <span className={`text-xs font-bold ${flash === 'up' ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
                            {flash === 'up' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>

                      {/* Bid/Ask spread */}
                      <div className="text-[10px] text-muted-foreground font-mono-data mt-0.5 truncate">
                        B:{bid.toFixed(decimals)} | A:{ask.toFixed(decimals)}
                      </div>

                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs font-medium ${isUp ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
                          {isUp ? '+' : ''}{row.change_percent.toFixed(2)}%
                        </span>
                        {hist.length > 1 && (
                          <Sparkline data={hist} width={40} height={14} positive={isUp} />
                        )}
                      </div>

                      {/* Activity bar */}
                      <div className="mt-1.5 h-[2px] w-full bg-muted/20 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${activityWidth}%`,
                            backgroundColor: isUp ? 'hsl(142, 76%, 36%)' : 'hsl(0, 84%, 60%)',
                          }}
                        />
                      </div>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    ~30s delay • Click for live chart
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </TooltipProvider>
  );
}
